import { useEffect, useMemo, useRef } from 'react';
import { getBootstrapData } from '@tpct/admin-ui';
import type { Report } from '@tpct/mission-core/types/report';
import { __ } from '@/hub-shared/utils/i18n';
import { loadVsmeBundle } from '@/hub-shared/utils/loadVsmeBundle';
import {
  mapUserOngoingMissionReports,
  type UserOngoingMissionReportApiRow,
} from '@/users/src/utils/mapUserOngoingMissionReports';

type UserOngoingMissionsSectionProps = {
  reports: UserOngoingMissionReportApiRow[] | undefined;
  bootstrapKey?: string;
  onRefresh?: () => void;
};

type ReportsTableApi = {
  mountTable: (
    container: HTMLElement,
    props: { reports: Report[]; loading?: boolean; onRefresh?: () => void; withContainer?: boolean },
  ) => void;
  unmountTable: () => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * The reports table is a vsme feature bundle rendered here at runtime (not source-compiled)
 * via `window.VSMEReportsAPI.mountTable` (MIG-015). The bundle self-guards on
 * `window.VSMEReportsAdmin`, so publish that bootstrap before loading the script.
 */
const ensureVsmeReportsBootstrap = (bootstrapKey: string): void => {
  if (typeof window.VSMEReportsAdmin !== 'undefined') {
    return;
  }

  const raw: unknown = getBootstrapData(bootstrapKey);
  if (!isRecord(raw)) {
    return;
  }

  const restUrl = typeof raw.restUrlVsme === 'string'
    ? raw.restUrlVsme
    : typeof raw.restUrl === 'string'
      ? raw.restUrl
      : '';
  const companiesRestUrl = typeof raw.restUrl === 'string' ? raw.restUrl : '';
  const nonce = typeof raw.nonce === 'string' ? raw.nonce : '';
  const currentUser = isRecord(raw.currentUser) ? raw.currentUser : { id: 0, roles: [] };

  window.VSMEReportsAdmin = {
    restUrl,
    companiesRestUrl,
    nonce,
    currentUser: {
      id: typeof currentUser.id === 'number' ? currentUser.id : 0,
      roles: Array.isArray(currentUser.roles)
        ? currentUser.roles.filter((role): role is string => typeof role === 'string')
        : [],
    },
    ajaxUrl: typeof raw.ajaxUrl === 'string' ? raw.ajaxUrl : '',
    adminUrl: typeof raw.adminUrl === 'string' ? raw.adminUrl : '',
  };
};

const readReportsBundleUrl = (bootstrapKey: string): string => {
  const raw: unknown = getBootstrapData(bootstrapKey);
  return isRecord(raw) && typeof raw.vsmeReportsBundleUrl === 'string'
    ? raw.vsmeReportsBundleUrl
    : '';
};

const UserOngoingMissionsSection = ({
  reports,
  bootstrapKey = 'AccountingHubPortal',
  onRefresh,
}: UserOngoingMissionsSectionProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const mappedReports: Report[] = useMemo(
    () => mapUserOngoingMissionReports(reports),
    [reports],
  );
  const hasReports = mappedReports.length > 0;

  // Latest data in a ref so the mount effect can read it without listing it as a dep (which
  // would tear the table down on every refresh, resetting its uncontrolled pagination).
  const mappedRef = useRef(mappedReports);
  mappedRef.current = mappedReports;
  const apiReadyRef = useRef(false);

  // Mount lifecycle (stable deps): load the bundle, mount once, unmount only on real unmount /
  // when the section transitions to empty. onRefresh churn and data updates do NOT re-run this
  // (data is read via mappedRef so a new reports array does not tear the table down).
  useEffect(() => {
    if (!hasReports) {
      return;
    }

    ensureVsmeReportsBootstrap(bootstrapKey);
    const bundleUrl = readReportsBundleUrl(bootstrapKey);
    if (!bundleUrl) {
      console.error('VSME reports bundle URL missing from portal bootstrap.');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        await loadVsmeBundle('reports', bundleUrl, 'VSMEReportsAPI', __('Impossible de charger le module VS.'));
        if (cancelled) {
          return;
        }
        const api = (window as unknown as Record<string, ReportsTableApi>).VSMEReportsAPI;
        const container = containerRef.current;
        if (api?.mountTable && container) {
          apiReadyRef.current = true;
          api.mountTable(container, {
            reports: mappedRef.current,
            loading: false,
            onRefresh: () => onRefreshRef.current?.(),
            withContainer: false,
          });
        }
      } catch (err) {
        console.error('VSME reports bundle load error:', err);
      }
    })();

    return () => {
      cancelled = true;
      apiReadyRef.current = false;
      (window as unknown as Record<string, ReportsTableApi>).VSMEReportsAPI?.unmountTable?.();
    };
  }, [bootstrapKey, hasReports]);

  // Data updates: re-render the mounted table in place (mountTable reconciles when the
  // container is unchanged, preserving pagination) when reports change after the bundle is
  // ready. Never tears the root down.
  useEffect(() => {
    if (!hasReports || !apiReadyRef.current) {
      return;
    }
    const api = (window as unknown as Record<string, ReportsTableApi>).VSMEReportsAPI;
    const container = containerRef.current;
    if (api?.mountTable && container) {
      api.mountTable(container, {
        reports: mappedReports,
        loading: false,
        onRefresh: () => onRefreshRef.current?.(),
        withContainer: false,
      });
    }
  }, [mappedReports, hasReports]);

  return (
    <section className="user-ongoing-missions" aria-label={__('Missions en cours')}>
      <div className="container-style user-ongoing-missions-panel">
        <h3 className="user-ongoing-missions-panel__title">{__('Missions en cours')}</h3>
        {mappedReports.length === 0 ? (
          <p className="user-ongoing-missions-panel__empty">{__('Aucune mission en cours')}</p>
        ) : (
          <div ref={containerRef} className="user-ongoing-missions-panel__table-mount" />
        )}
      </div>
    </section>
  );
};

export default UserOngoingMissionsSection;
